import { useState, useCallback } from 'react';
import { EmailMessageSummary, EmailMessage } from '../types';
import { emailApi } from '../services/communicationsApi';

export const useEmailMessages = () => {
  const [messages, setMessages] = useState<EmailMessageSummary[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<EmailMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = useCallback(async (channelId: number, mailbox: string) => {
    setLoading(true);
    setError(null);
    setMessages([]);
    setSelectedMessage(null);
    try {
      const data = await emailApi.getMessages(channelId, mailbox);
      setMessages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const syncMessages = useCallback(async (channelId: number, mailbox: string) => {
    setSyncing(true);
    setError(null);
    try {
      const result = await emailApi.sync(channelId, mailbox);
      setMessages(result.messages);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      setError(msg);
      throw err;
    } finally {
      setSyncing(false);
    }
  }, []);

  const fetchMessage = useCallback(async (
    channelId: number,
    mailbox: string,
    uid: number,
    summary?: EmailMessageSummary
  ) => {
    setError(null);
    if (summary) {
      setSelectedMessage({
        uid: summary.uid,
        mailbox: summary.mailbox,
        date: summary.date,
        from: summary.from,
        subject: summary.subject,
        isRead: true
      });
      setMessages(prev =>
        prev.map(m => (m.uid === uid && m.mailbox === mailbox ? { ...m, isRead: true } : m))
      );
    }
    setMessageLoading(true);
    try {
      const msg = await emailApi.getMessage(channelId, mailbox, uid);
      setSelectedMessage(msg);
      setMessages(prev =>
        prev.map(m => (m.uid === uid && m.mailbox === mailbox ? { ...m, isRead: true } : m))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch message');
    } finally {
      setMessageLoading(false);
    }
  }, []);

  const clearSelectedMessage = useCallback(() => {
    setSelectedMessage(null);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setSelectedMessage(null);
    setError(null);
  }, []);

  const removeMessageFromList = useCallback((mailbox: string, uid: number) => {
    setMessages(prev => prev.filter(m => !(m.mailbox === mailbox && m.uid === uid)));
    setSelectedMessage(prev =>
      prev && prev.mailbox === mailbox && prev.uid === uid ? null : prev
    );
  }, []);

  const updateMessageReadState = useCallback((mailbox: string, uid: number, isRead: boolean) => {
    setMessages(prev =>
      prev.map(m => (m.uid === uid && m.mailbox === mailbox ? { ...m, isRead } : m))
    );
    setSelectedMessage(prev =>
      prev && prev.mailbox === mailbox && prev.uid === uid ? { ...prev, isRead } : prev
    );
  }, []);

  return {
    messages,
    selectedMessage,
    loading,
    syncing,
    messageLoading,
    error,
    fetchMessages,
    syncMessages,
    fetchMessage,
    clearSelectedMessage,
    clearMessages,
    removeMessageFromList,
    updateMessageReadState
  };
};
